import { useCallback, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { datadogRum } from '@datadog/browser-rum'
import { type AsyncInvokeAgentPartialResult, type AsyncInvokeAgentProgressSnapshot, AiUseCase } from '@einwert/valuation-api-client'
import { PdfValidator, type PdfValidatorWorkflowInvoker } from 'ComponentLibrary/src/playground'

import { useInvokeWorkflow } from 'ClientPortal/src/api/Queries/ai'
import { usePlaygroundFeedback } from './usePlaygroundFeedback'
import { usePlaygroundTranslations } from './usePlaygroundTranslations'

export const PdfValidatorPage = () => {
    const location = useLocation()
    const { feedbackButton } = usePlaygroundFeedback()
    const { dashboardBreadcrumbRoot, pdfValidatorCopy } = usePlaygroundTranslations()
    const workflowCallbacksRef = useRef<Pick<Parameters<PdfValidatorWorkflowInvoker>[0], 'onProgress' | 'onPartialResult'>>({})

    const eventContext = {
        app: 'client_portal',
        toolKey: 'pdf-validator',
        toolTitle: pdfValidatorCopy.title,
        path: location.pathname,
    }

    useEffect(() => {
        datadogRum.addAction('playground_tool_visited', eventContext)

        return () => {
            datadogRum.addAction('playground_tool_left', eventContext)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname])

    const forwardWorkflowProgress = useCallback((progress: AsyncInvokeAgentProgressSnapshot) => {
        workflowCallbacksRef.current.onProgress?.(progress)
    }, [])
    const forwardWorkflowPartialResult = useCallback((partialResult: AsyncInvokeAgentPartialResult) => {
        workflowCallbacksRef.current.onPartialResult?.(partialResult)
    }, [])
    const { mutateAsync } = useInvokeWorkflow({
        agentUseCase: AiUseCase.PLAUSIBILITY,
        onProgress: forwardWorkflowProgress,
        onPartialResult: forwardWorkflowPartialResult,
    })
    const invokeWorkflow = useCallback<PdfValidatorWorkflowInvoker>(
        async ({ onProgress, onPartialResult, ...request }) => {
            workflowCallbacksRef.current = { onProgress, onPartialResult }

            try {
                return await mutateAsync(request)
            } finally {
                workflowCallbacksRef.current = {}
            }
        },
        [mutateAsync]
    )

    return (
        <PdfValidator
            invokeWorkflow={invokeWorkflow}
            feedbackButton={feedbackButton}
            breadcrumbRoot={dashboardBreadcrumbRoot}
            copy={pdfValidatorCopy}
            onDataPrivacyModalOpen={() => datadogRum.addAction('ai_tool_data_privacy_modal_opened', eventContext)}
            onInfoModalOpen={() => datadogRum.addAction('ai_tool_info_modal_opened', eventContext)}
            onSamplePdfUsed={() => datadogRum.addAction('ai_tool_sample_pdf_used', eventContext)}
        />
    )
}
